import { IContextualMenuItem } from "@fluentui/react";
import React from "react";
import { useSelector } from "react-redux";
import { FileDownLoader } from "../../../src/taskpane/components/helpers/fileDownloader";
import { ComponentTypes } from "../../../src/taskpane/config/config";
import { OneNoteProgId, SharePointFolderContentTypeIds } from "../../../src/taskpane/config/constants";
import { RootState } from "../../../src/taskpane/redux";
import { getFileItemIdFromRelativePath, getItemWebViewUrl } from "../../../src/taskpane/services/GraphService";
import authModule from "../../../utilities/AuthModule";
import { LanguageContext } from "../../../utilities/LocalizationModule";
import { logger } from "../../../utilities/logger";
import { getFileNameDownloadTemplate, getItemCurrentVersion, getItemFieldTextValue, getWildcardValues, sanitizeDownloadFileName, versionLabelInternalName } from "../../../utilities/FileNameDownloadTemplateHelper";

export function useMenuItem_Download(listFields: any, selectedItems: any[], waitFor: boolean, setWaitFor: (waitFor: boolean) => void, isInOneDrive: boolean, setWaitForContext) {
  const languageStrings = React.useContext(LanguageContext);

  const currentDirectory = useSelector((state: RootState) => state.workingDirectories.workingDirectories.length > 0 ? state.workingDirectories.workingDirectories[state.workingDirectories.workingDirectories.length - 1] : null);
  const oneDriveRootDetails = useSelector((state: RootState) => state.workingDirectories.oneDriveRootDetails);
  const isBusySaving = useSelector((state: RootState) => state.savingInBackground.isBusySaving);
  const siteInfo = useSelector((state: RootState) => state.tenantInfo?.sites);
  const currentSharepointFolder = useSelector((state: RootState) => state.currentSharepointFolder);

  const [menuItem_Download, setMenuItem_Download] = React.useState<IContextualMenuItem>();

  React.useEffect(() => {
    if (selectedItems?.length > 0
      && !(selectedItems.some(x => x?.File_x0020_Type === "url"))
      && !(selectedItems.some(x => x?.EffectiveBasePermissions?.canOpenItems === false))
      && !(selectedItems.some(x => x.ContentTypeId?.startsWith(SharePointFolderContentTypeIds.folder)) || selectedItems.some(x => x.ProgId === OneNoteProgId)) && currentDirectory.type !== ComponentTypes.SharePointList) {
      setMenuItem_Download(
        {
          key: "download",
          disabled: (waitFor === true || isBusySaving === true),
          text: `${languageStrings.Download}`,
          iconProps: { iconName: "Download" },
          onClick: () => {
            downloadFile();
          }
        }
      )
    } else {
      setMenuItem_Download(null);
    }
  }, [selectedItems, waitFor, currentDirectory]);

  const downloadFile = async () => {
    setWaitForContext(languageStrings.Downloading);
    setWaitFor(true);

    try {
      let listId: string;
      let webUrl: string;
      let fileNameTemplateString: string;
      let wildcardValues: string[];
      if (!isInOneDrive) {
        listId = currentSharepointFolder?.sharepointListId ?? currentDirectory?.sharePointListId;
        webUrl = currentDirectory?.webUrl;

        fileNameTemplateString = getFileNameDownloadTemplate(siteInfo, currentDirectory?.siteDisplayName, currentDirectory?.DocumentLibrary);
        if (fileNameTemplateString) {
          wildcardValues = getWildcardValues(fileNameTemplateString);
        }
      }

      const driveId = !isInOneDrive ? (!currentDirectory.parentId ? currentDirectory.id : currentDirectory.parentId) : oneDriveRootDetails.driveId;
      const driveItems = await Promise.all(selectedItems.map(async (item) => {
        const spItemUrlparts = item['.spItemUrl'].split('/');
        let itemId: string = spItemUrlparts[spItemUrlparts.length - 1].split('?')[0];
        if (!itemId) {
          const driveUrl = new URL(await getItemWebViewUrl(await authModule.getGraphAccessToken(), driveId, null));
          const folderUrl = new URL(driveUrl.origin + item.FileRef);
          itemId = await getFileItemIdFromRelativePath(await authModule.getGraphAccessToken(), driveId, folderUrl.pathname.replace(driveUrl.pathname, ""), false);
        }

        let fileName: string;
        if (fileNameTemplateString && Array.isArray(wildcardValues) && wildcardValues.length > 0 && listFields) {
          let itemValues: any;
          if (item && webUrl && listId) {
            const itemUniqueId: string = item?.UniqueId?.replace("{", "").replace("}", "");
            if (itemUniqueId) {
              const itemCurrentVersion = await getItemCurrentVersion(await authModule.getSharePointAccessToken(), webUrl, listId, itemUniqueId);
              if (itemCurrentVersion) {
                itemValues = itemCurrentVersion;
              }
            }
          }

          fileName = fileNameTemplateString;
          wildcardValues.forEach(wildcardValue => {
            const field = wildcardValue === versionLabelInternalName
              ? { InternalName: versionLabelInternalName }
              : listFields.value.find(x => x.Title === wildcardValue || x.InternalName === wildcardValue);

            fileName = fileName.replaceAll(`<<[${wildcardValue}]>>`, getItemFieldTextValue(itemValues ?? item, field) ?? "");
          });
          fileName += "." + item[".fileType"];
        }
        else {
          fileName = item.FileLeafRef;
        }

        return {
          driveId: driveId,
          itemId: itemId,
          name: sanitizeDownloadFileName(fileName)
        };
      }));

      const downloadRequestPayload = {
        accessToken: await authModule.getGraphAccessToken(),
        items: driveItems
      };

      await FileDownLoader.downloadFileStream(downloadRequestPayload);
    } catch (error) {
      logger(error, "useMenuItem_Download.tsx (downloadFile)");
    }

    setWaitFor(false);
    setWaitForContext(languageStrings?.LoadingOnly);
  }

  return {
    menuItem_Download
  }

}